import { Fragment, useState } from 'react';

import { TextInput } from '@/components/ui/daisy';
import { useInputFocus } from '@/data/hook/shared/useInputFocus';
import { KeyboardKeys } from '@/utils/Keyboard';
import AnswerConfirmEdit from './AnswerConfirmEdit';
import { preventDefault } from '@/utils/mouseEvent';

type AnswerTextInputProps = {
  initialValue: string;
  placeholder?: string;
  onConfirm: (value: string) => void;
  onCancel?: () => void;
};

export default function AnswerTextInput({
  initialValue,
  placeholder,
  onConfirm,
  onCancel,
}: AnswerTextInputProps) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useInputFocus<HTMLInputElement>();

  const handleConfirm = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setValue(initialValue);
      onCancel?.();
      return;
    }
    if (trimmed === initialValue) {
      onCancel?.();
      return;
    }
    onConfirm(trimmed);
  };

  const handleKeyEnter = (e: React.KeyboardEvent) => {
    preventDefault(e);
    handleConfirm();
  };

  const handleKeyEscape = (e: React.KeyboardEvent) => {
    preventDefault(e);
    setValue(initialValue);
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const keyboardKey = e.key as Extract<KeyboardKeys, 'Enter' | 'Escape'>;

    const map = {
      [KeyboardKeys.Enter]: handleKeyEnter,
      [KeyboardKeys.Escape]: handleKeyEscape,
    };

    map[keyboardKey]?.(e);
  };

  return (
    <Fragment>
      <TextInput
        ref={inputRef}
        type="text"
        size="sm"
        className="flex-1 text-base-content text-base"
        placeholder={placeholder}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleConfirm}
      />
      <AnswerConfirmEdit onConfirm={handleConfirm} />
    </Fragment>
  );
}
